import type { Container } from 'pixi.js';
import type { FXParticleLayer } from '../../../../libs/particles/FXParticleLayer';
import type { TileStackView } from './TileStackView';
import type { TileRemovalReason } from '../../models/board/BoardEvents';
import { TILE_MATCH_REASONS } from '../../models/board/BoardEvents';
import { soundManager } from '../../../../app/SoundManager';
import TileRemovalParticlesJSON from '../../../../../assets/particles{ignore}/Tile_Remove_Particles.json';
import type { FXParticleEmitterConfig } from '../../../../libs/particles/FXParticleEmitterConfig';
import { GAME_OBJECT_POOL } from '../../../GameConstants';
import type { TileView } from './TileView';
import type { EasingType } from '../../../../libs/TweenManager/easing';

interface TileMatchAnimatorConfig {
  stackContainer: Container;
  particleLayer: FXParticleLayer;
}

interface MatchAnimationConfig {
  stackView: TileStackView;
  tileIds: string[];
  reason: TileRemovalReason;
}

interface TileHighlightAnimationConfig {
  tileViews: TileView[];
  delayBetweenTiles: number;
}

interface ParticleAnimationConfig {
  stackView: TileStackView;
  tileViews: TileView[];
  delayBetweenTiles: number;
}

interface TileScaleAnimationConfig {
  stackView: TileStackView;
  tileViews: TileView[];
  delayBetweenTiles: number;
  playSound: boolean;
}

interface IntensityAnimationConfig {
  stackView: TileStackView;
  tileViews: TileView[];
  playSound: boolean;
}

interface TweenConfig {
  duration?: number;
  target?: number;
  easing?: string;
  delay?: number;
}

interface TileMatchAnimationConfig {
  bigHex: {
    scaleTween: TweenConfig;
    alphaTween: TweenConfig;
  };
  towerHighlight: {
    fadeInTween: TweenConfig;
    fadeOutTween: TweenConfig;
  };
  tile: {
    scaleTween: TweenConfig;
  };
  particles: {
    particleOffsetY: number;
  };
  delayBetweenHighlightAndRemoval: number;
  delayBetweenTiles: number;
}

export class TileRemovalAnimator {
  public static animationConfig: TileMatchAnimationConfig = {
    bigHex: {
      scaleTween: {
        duration: 800,
        target: 1.2,
        easing: 'cubicOut',
      },
      alphaTween: {
        delay: 600,
        easing: 'sinIn',
        duration: 200,
      },
    },
    towerHighlight: {
      fadeInTween: {
        easing: 'sinIn',
        duration: 400,
      },
      fadeOutTween: {
        delay: 800,
        easing: 'sinOut',
        duration: 300,
      },
    },
    tile: {
      scaleTween: {
        easing: 'sinIn',
        duration: 360,
      },
    },
    particles: {
      particleOffsetY: 10,
    },
    delayBetweenHighlightAndRemoval: 400,
    delayBetweenTiles: 60,
  };

  constructor(private readonly config: TileMatchAnimatorConfig) {}

  public async animateTileRemoval({ stackView, tileIds, reason }: MatchAnimationConfig): Promise<void> {
    const {
      animationConfig: { delayBetweenTiles, delayBetweenHighlightAndRemoval },
    } = TileRemovalAnimator;

    const tileViews: TileView[] = stackView.prepareTilesForRemoval(tileIds);

    const isMatchReason = TILE_MATCH_REASONS.includes(reason);
    const delayBetweenTilesScaled = delayBetweenTiles * (isMatchReason ? 1 : 0.5);

    const config = {
      tileViews,
      delayBetweenTiles: delayBetweenTilesScaled,
      stackView,
      playSound: isMatchReason,
    };

    this.animateIntensityDecorations(config);

    void this.animateTileHighlight(config);

    await stackView.wait(delayBetweenHighlightAndRemoval);

    if (isMatchReason) void this.animateParticles(config);

    await this.animateTileScale(config);

    tileViews.forEach((tileView) => {
      tileView.destroy({ children: true });
    });
    stackView.reset();
  }

  private animateIntensityDecorations({ stackView, tileViews, playSound }: IntensityAnimationConfig): void {
    const showBigHexHighlight = tileViews.length >= 12;
    const showTowerHighlight = tileViews.length >= 14;

    if (playSound) {
      if (showTowerHighlight) {
        soundManager.play('Gameplay_Match_3');
      } else if (showBigHexHighlight) {
        soundManager.play('Gameplay_Match_2');
      } else {
        soundManager.play('Gameplay_Match_1');
      }
    }

    if (showBigHexHighlight) void this.animateHexHighlight(stackView);
    if (showTowerHighlight) void this.animateTowerHighlight(stackView);
  }

  private async animateParticles({ stackView, tileViews, delayBetweenTiles }: ParticleAnimationConfig): Promise<void> {
    const { particleLayer } = this.config;
    const {
      animationConfig: {
        particles: { particleOffsetY },
      },
    } = TileRemovalAnimator;
    const emitter = particleLayer.addParticleEmitter(0, 0, TileRemovalParticlesJSON as FXParticleEmitterConfig);

    emitter.container.props({
      parent: stackView,
    });

    await Promise.all(
      tileViews.map(async (tileView, index) => {
        await tileView.wait(delayBetweenTiles * index);
        if (tileView.destroyed) return;
        emitter.emitY = tileView.y + particleOffsetY;

        const isLastTile = tileViews.length === index + 1;
        if (isLastTile) {
          emitter.stop();
        }
      }),
    );
  }

  private async animateTileHighlight({ tileViews, delayBetweenTiles }: TileHighlightAnimationConfig): Promise<void> {
    await Promise.all(
      tileViews.map(async (tileView, index) => {
        await tileView.wait(delayBetweenTiles * index);

        if (tileView.destroyed) return;

        const highlight = GAME_OBJECT_POOL.getSprite().props({
          parent: tileView,
          alpha: 0,
          anchor: 0.5,
          texture: 'Tile_Highlight',
        });

        await highlight.tweenTo({ alpha: 1 }, 200, 'sinInOut', true, 0, 0, true).asyncOnComplete();
        GAME_OBJECT_POOL.addSprite(highlight);
      }),
    );
  }

  private async animateTileScale({
    stackView,
    tileViews,
    delayBetweenTiles,
    playSound,
  }: TileScaleAnimationConfig): Promise<void> {
    const {
      animationConfig: {
        tile: {
          scaleTween: { easing, duration },
        },
      },
    } = TileRemovalAnimator;

    await Promise.all(
      tileViews.map(async (tileView, index) => {
        await tileView.wait(delayBetweenTiles * index);
        if (playSound) soundManager.play(`Gameplay_Hex_Remove`);
        if (tileView.destroyed) return;

        await tileView
          .tweenTo(
            {
              scaleX: 0,
              scaleY: 0,
            },
            duration ?? 300,
            (easing as EasingType) ?? 'sinIn',
            true,
          )
          .asyncOnComplete();

        stackView.removeTile(tileView);
      }),
    );
  }

  private async animateTowerHighlight(stackView: TileStackView): Promise<void> {
    if (stackView.destroyed) return;

    const {
      config: { stackContainer },
    } = this;
    const {
      animationConfig: {
        towerHighlight: { fadeInTween, fadeOutTween },
      },
    } = TileRemovalAnimator;

    const tower = GAME_OBJECT_POOL.getSprite().props({
      parent: stackContainer,
      anchor: { x: 0.5, y: 1 },
      x: stackView.x,
      y: stackView.y + 120,
      alpha: 0,
      texture: 'Stack_Match_Highlight',
      zIndex: stackView.zIndex + 1,
    });

    tower.tweenTo(
      {
        alpha: 1,
      },
      fadeInTween.duration,
      fadeInTween.easing as EasingType,
      true,
    );

    await stackView.wait(fadeOutTween.delay ?? 200);

    await tower
      .tweenTo(
        {
          alpha: 0,
        },
        fadeOutTween.duration,
        fadeOutTween.easing as EasingType,
        true,
      )
      .asyncOnComplete();

    GAME_OBJECT_POOL.addSprite(tower);
  }

  private async animateHexHighlight(stackView: TileStackView): Promise<void> {
    if (stackView.destroyed) return;

    const {
      config: { stackContainer },
    } = this;
    const {
      animationConfig: {
        bigHex: {
          scaleTween: { target: scaleTarget, duration: scaleDuration, easing: scaleEasing },
          alphaTween: { delay: alphaDelay, easing: alphaEasing, duration: alphaDuration },
        },
      },
    } = TileRemovalAnimator;

    const hex = GAME_OBJECT_POOL.getSprite().props({
      texture: 'Match_Hexagon',
      anchor: 0.5,
      zIndex: stackView.zIndex - 1,
      scale: 0,
      x: stackView.x,
      y: stackView.y,
      parent: stackContainer,
    });

    const scaleTween = hex.tweenTo(
      {
        scaleX: scaleTarget,
        scaleY: scaleTarget,
      },
      scaleDuration,
      scaleEasing as EasingType,
      true,
    );

    const alphaTween = hex.tweenTo(
      {
        alpha: 0,
      },
      alphaDuration,
      alphaEasing as EasingType,
      true,
      alphaDelay,
    );

    await Promise.all([scaleTween.asyncOnComplete(), alphaTween.asyncOnComplete()]);

    GAME_OBJECT_POOL.addSprite(hex);
  }
}
